import { isValid, parseISO } from 'date-fns';

import type { DateTimePrecision } from '../shared/common';
import { createError, pad } from '../shared/common';
import type { DateTimeZonedType, ParsedDateTimeZoned } from './types';

const OFFSET_SUFFIX_REGEX = /(Z|[+-]\d{2}:\d{2})$/;

export const detectPrecision = (value: string): DateTimePrecision => {
  const timePart = value.split('T')[1]?.replace(OFFSET_SUFFIX_REGEX, '') ?? '';
  if (timePart.includes('.')) return 'milliseconds';
  if (timePart.split(':').length === 3) return 'seconds';
  return 'minutes';
};

export const extractOffsetSuffix = (value: string): string => {
  const offsetSuffix = value.match(OFFSET_SUFFIX_REGEX)?.[0];
  if (!offsetSuffix)
    throw createError(
      `Ожидалась ISO-дата и время с таймзоной, получено: ${value}`,
    );
  return offsetSuffix;
};

export const getOffsetMinutes = (offsetSuffix: string): number => {
  if (offsetSuffix === 'Z') return 0;
  const sign = offsetSuffix[0] === '-' ? -1 : 1;
  const [hours, minutes] = offsetSuffix.slice(1).split(':').map(Number);
  return sign * (hours * 60 + minutes);
};

export const formatDateTimeWithOffset = (
  date: Date,
  precision: DateTimePrecision,
  offsetSuffix: string,
): string => {
  const offsetMinutes = getOffsetMinutes(offsetSuffix);
  const shiftedDate = new Date(date.getTime() + offsetMinutes * 60_000);
  const base = `${pad(shiftedDate.getUTCFullYear(), 4)}-${pad(
    shiftedDate.getUTCMonth() + 1,
  )}-${pad(shiftedDate.getUTCDate())}T${pad(shiftedDate.getUTCHours())}:${pad(
    shiftedDate.getUTCMinutes(),
  )}`;

  if (precision === 'minutes') return `${base}${offsetSuffix}`;

  const withSeconds = `${base}:${pad(shiftedDate.getUTCSeconds())}`;
  if (precision === 'seconds') return `${withSeconds}${offsetSuffix}`;

  return `${withSeconds}.${pad(
    shiftedDate.getUTCMilliseconds(),
    3,
  )}${offsetSuffix}`;
};

export const parseDateTime = (value: string): Date => {
  const date = parseISO(value);
  if (!isValid(date))
    throw createError(
      `Ожидалась корректная ISO-дата и время, получено: ${value}`,
    );
  return date;
};

export const toOriginalTimeZoneNativeDate = (value: string): Date => {
  const offsetMinutes = getOffsetMinutes(extractOffsetSuffix(value));
  return new Date(parseDateTime(value).getTime() + offsetMinutes * 60_000);
};

export const parseDateTimeZoned = (
  value: DateTimeZonedType,
): ParsedDateTimeZoned => ({
  value,
  date: parseDateTime(value),
  precision: detectPrecision(value),
  offsetSuffix: extractOffsetSuffix(value),
});
